import { GreenScoreBadge } from "@/components/public/green-score-badge"
import ComparisonRow from "@/components/public/comparison-row"
import type { GreenScore } from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw report shape from Rails (snake_case keys)                       */
/* ------------------------------------------------------------------ */

interface RawReport {
  postcode: {
    postcode: string
    admin_district: string
  } | null
  crime: { total_crimes: number } | null
  property: { current_average: number | null } | null
  amenities: {
    schools: Array<{ name: string; type?: string }>
    parks: Array<{ name: string }>
    restaurants: Array<{ name: string; cuisine?: string }>
    shops: number
    transport_stops: number
    cycleways: number
  } | null
  green_score: {
    total: number
    breakdown: {
      green_space: number
      flood_risk: number
      conservation: number
      walkability: number
      cycling: number
    }
  } | null
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/* Mapper                                                              */
/* ------------------------------------------------------------------ */

function mapGreenScore(raw: RawReport["green_score"]): GreenScore | null {
  if (!raw) return null
  return {
    total: raw.total,
    breakdown: {
      greenSpace: raw.breakdown.green_space,
      floodRisk: raw.breakdown.flood_risk,
      conservation: raw.breakdown.conservation,
      walkability: raw.breakdown.walkability,
      cycling: raw.breakdown.cycling,
    },
  }
}

/* ------------------------------------------------------------------ */
/* Embed component (no PublicLayout)                                    */
/* ------------------------------------------------------------------ */

interface PageProps {
  postcodes: string[]
  reports: Record<string, RawReport | null>
  error?: string
}

export default function CompareEmbedPage({
  postcodes,
  reports,
  error,
}: PageProps) {
  if (error) {
    return (
      <div className="p-6 font-sans text-sm text-slate">
        <p>{error}</p>
      </div>
    )
  }

  const reportA = postcodes.length >= 2 ? reports[postcodes[0]] ?? null : null
  const reportB = postcodes.length >= 2 ? reports[postcodes[1]] ?? null : null

  if (!reportA || !reportB) {
    return (
      <div className="p-6 font-sans text-sm text-slate">
        <p>No comparison data available.</p>
      </div>
    )
  }

  const greenScoreA = mapGreenScore(reportA.green_score)
  const greenScoreB = mapGreenScore(reportB.green_score)

  return (
    <div className="mx-auto max-w-xl p-6 font-sans">
      {/* Header */}
      <div className="grid grid-cols-3 items-center gap-4">
        <div className="flex flex-col items-center text-center">
          <p className="text-base font-bold text-charcoal">{postcodes[0]}</p>
          {greenScoreA && (
            <div className="mt-2">
              <GreenScoreBadge score={greenScoreA} size="sm" />
            </div>
          )}
        </div>
        <div className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate">
            vs
          </span>
        </div>
        <div className="flex flex-col items-center text-center">
          <p className="text-base font-bold text-charcoal">{postcodes[1]}</p>
          {greenScoreB && (
            <div className="mt-2">
              <GreenScoreBadge score={greenScoreB} size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Comparison rows */}
      <div className="mt-6 rounded-lg border border-stone p-4">
        <ComparisonRow
          label="Avg price"
          valueA={reportA.property?.current_average ?? null}
          valueB={reportB.property?.current_average ?? null}
          format="currency"
          higherIsBetter={false}
        />
        <div className="border-t border-stone/50" />

        <ComparisonRow
          label="Crime"
          valueA={reportA.crime?.total_crimes ?? null}
          valueB={reportB.crime?.total_crimes ?? null}
          format="number"
          higherIsBetter={false}
        />
        <div className="border-t border-stone/50" />

        <ComparisonRow
          label="Green score"
          valueA={reportA.green_score?.total ?? null}
          valueB={reportB.green_score?.total ?? null}
          format="number"
          higherIsBetter={true}
        />
        <div className="border-t border-stone/50" />

        <ComparisonRow
          label="Schools"
          valueA={reportA.amenities?.schools?.length ?? null}
          valueB={reportB.amenities?.schools?.length ?? null}
          format="number"
          higherIsBetter={true}
        />
        <div className="border-t border-stone/50" />

        <ComparisonRow
          label="Parks"
          valueA={reportA.amenities?.parks?.length ?? null}
          valueB={reportB.amenities?.parks?.length ?? null}
          format="number"
          higherIsBetter={true}
        />
      </div>

      {/* Powered by */}
      <p className="mt-4 text-center text-xs text-slate">
        Powered by{" "}
        <a
          href="https://quiteright.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-red hover:text-charcoal"
        >
          QuiteRight
        </a>
      </p>
    </div>
  )
}
